import ClipIcon from '@src/svgs/quick-actions/VideoIcon';
import StoryIcon from '@src/svgs/VideoIcon';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import FolderIcon from './StoryManagerFolderView/FolderIcon';

import type {
  SearchDropdownResult,
  SearchDropdownResults,
} from './searchDropdownResults';
import type { CSSProperties, FC, RefObject } from 'react';

type Props = {
  isOpen: boolean;
  /** Ref + style from `useAnchoredDropdown` — the body-portal positioning contract. */
  menuRef: RefObject<HTMLDivElement>;
  style: CSSProperties | null;
  /** The (debounced) query the results correspond to — shown in the no-results copy. */
  query: string;
  results: SearchDropdownResults;
  /** Index into `results.flat` of the keyboard-active row; -1 = none. */
  activeIndex: number;
  onSelect: (result: SearchDropdownResult) => void;
};

/**
 * Google Drive-style results dropdown under the Story Manager search input.
 * Purely presentational: grouping/order comes from `buildSearchDropdownResults`
 * and open/keyboard state from `useSearchResultsDropdown`.
 */
const SearchResultsDropdown: FC<Props> = ({
  isOpen,
  menuRef,
  style,
  query,
  results,
  activeIndex,
  onSelect,
}) => {
  // Keep the keyboard-active row visible as the arrows move it past the fold.
  useEffect(() => {
    if (activeIndex < 0) return;
    menuRef.current
      ?.querySelector('[data-active="true"]')
      ?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, menuRef]);

  if (!isOpen || !style) return null;

  const { folderResults, storyResults, clipResults, flat } = results;
  // Group offsets into the flat list — keyboard order matches render order.
  const storyOffset = folderResults.length;
  const clipOffset = storyOffset + storyResults.length;

  const rowProps = (index: number, result: SearchDropdownResult) => ({
    $active: activeIndex === index,
    'data-active': activeIndex === index ? 'true' : undefined,
    onClick: () => onSelect(result),
  });

  return createPortal(
    <Container
      ref={menuRef}
      style={style}
      data-testid="search-results-dropdown"
    >
      {flat.length === 0 ? (
        <NoResults>No results for “{query}”</NoResults>
      ) : (
        <>
          {folderResults.length > 0 && (
            <SectionHeader>Folders ({folderResults.length})</SectionHeader>
          )}
          {folderResults.map((folder, i) => (
            <ResultRow
              key={folder.id}
              type="button"
              data-testid={`search-result-folder-${folder.id}`}
              {...rowProps(i, { type: 'folder', folder })}
            >
              <IconSlot>
                <FolderIcon />
              </IconSlot>
              <ResultContent>
                <ResultTitle>{folder.name}</ResultTitle>
              </ResultContent>
            </ResultRow>
          ))}

          {storyResults.length > 0 && (
            <SectionHeader>Stories ({storyResults.length})</SectionHeader>
          )}
          {storyResults.map(({ story, folderPath, matchLabel }, i) => (
            <ResultRow
              key={story.id}
              type="button"
              data-testid={`search-result-story-${story.id}`}
              {...rowProps(storyOffset + i, { type: 'story', story })}
            >
              <IconSlot>
                <StoryIcon strokeColor="currentColor" />
              </IconSlot>
              <ResultContent>
                <ResultTitle>{story.title}</ResultTitle>
                {/* The folder places the story; `matchLabel` says what the
                    query actually hit when it wasn't the title, so a row can
                    never appear for a reason nothing on screen explains. */}
                <ResultSubtitle>
                  in {folderPath}
                  {matchLabel ? ` · ${matchLabel}` : ''}
                </ResultSubtitle>
              </ResultContent>
            </ResultRow>
          ))}

          {clipResults.length > 0 && (
            <SectionHeader>Clips ({clipResults.length})</SectionHeader>
          )}
          {clipResults.map(({ clip, story }, i) => (
            <ResultRow
              key={clip.id}
              type="button"
              data-testid={`search-result-clip-${clip.id}`}
              {...rowProps(clipOffset + i, { type: 'clip', clip, story })}
            >
              <IconSlot>
                <ClipIcon />
              </IconSlot>
              <ResultContent>
                <ResultTitle>{clip.title || 'Untitled clip'}</ResultTitle>
                <ResultSubtitle>in {story.title}</ResultSubtitle>
              </ResultContent>
            </ResultRow>
          ))}
        </>
      )}
    </Container>,
    document.body,
  );
};

export default SearchResultsDropdown;

// Position, size, and z-index come from useAnchoredDropdown's inline style.
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 4px;
  background: var(--popover);
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: var(--elevation-toast);
`;

const SectionHeader = styled.div`
  padding: 8px 12px 4px;
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-semi-bold);
  color: var(--text-muted);
`;

const ResultRow = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  width: 100%;
  min-height: 36px;
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  background: ${({ $active }) =>
    $active ? 'var(--surface-elevated)' : 'transparent'};
  cursor: pointer;
  text-align: left;
  transition: background 120ms ease;

  &:hover {
    background: var(--surface-elevated);
  }
`;

const IconSlot = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  color: var(--text-muted);

  svg {
    width: 16px;
    height: 16px;
  }
`;

const ResultContent = styled.span`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const ResultTitle = styled.span`
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  color: var(--foreground);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ResultSubtitle = styled.span`
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  color: var(--text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const NoResults = styled.div`
  padding: 12px;
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  color: var(--text-muted);
`;
